import { ProfilePicture, Loader } from '@getro/rombo';
import { Trash, UserCog } from 'lucide-react';
import React, { useMemo, useRef } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import AdminDataTableActionItem from '../AdminDataTableActionItem';

export const useAdminData = ({
  admins = [],
  onRemoveAdmin,
  onUpdateLinkedinSync,
  onResendInvitation,
  currentUserId,
  canRemoveCompanyAdmins,
  onUpdateRole,
  isCompany,
  isResendingInvitation,
  userToResendInvitation,
}) => {
  const menuRef = useRef();
  const columns = [
    {
      Header: 'Member',
      accessor: 'name',
      width: isCompany ? '80%' : '40%',
      disableSortBy: true,
      show: true,
    },
    ...(!isCompany
      ? [
          {
            Header: 'Role',
            accessor: 'role',
            width: '20%',
            disableSortBy: true,
            show: true,
          },
          {
            Header: 'Contacts sharing',
            accessor: 'linkedinSyncStatus',
            width: '35%',
            disableSortBy: true,
            show: true,
          },
          {
            Header: 'Last LinkedIn sync',
            accessor: 'lastSyncedAt',
            width: '15%',
            disableSortBy: true,
            show: true,
            sx: {
              justifyContent: 'flex-end',
            },
          },
          {
            Header: 'Shared contacts',
            accessor: 'contactsSynced',
            width: '10%',
            disableSortBy: true,
            show: true,
            sx: {
              justifyContent: 'flex-start',
            },
          },
        ]
      : [
          {
            Header: '',
            accessor: 'invitationStatus',
            width: '35%',
            disableSortBy: true,
            show: true,
          },
        ]),
    {
      Header: '',
      accessor: 'actions',
      width: '5%',
      disableSortBy: true,
      show: canRemoveCompanyAdmins,
    },
  ];

  const actions = useMemo(
    () => [
      {
        title: 'Edit role',
        icon: UserCog,
        onClick: (e) => onUpdateRole(e, 'edit-role'),
        show: () => canRemoveCompanyAdmins && !isCompany && !!onUpdateRole,
      },
      {
        title: (admin) => (admin.role === 'ally' ? 'Remove ally' : 'Remove admin'),
        icon: Trash,
        onClick: (e) => onRemoveAdmin(e, 'remove'),
        show: () => canRemoveCompanyAdmins,
        sx: {
          color: 'red.600',
        },
      },
    ],
    [canRemoveCompanyAdmins, isCompany, onRemoveAdmin, onUpdateRole],
  );

  const statusMap = useMemo(
    () => ({
      active: {
        color: 'icon.success',
        text: 'Active',
      },
      paused: {
        color: 'neutral.300',
        text: 'Paused',
      },
      reconnection_needed: {
        color: 'orange.600',
        text: 'Reconnection needed',
      },
      not_completed: {
        color: 'neutral.300',
        text: 'Not connected',
      },
      not_sharing: {
        color: 'neutral.300',
        text: 'Not sharing',
      },
      not_shared: {
        color: 'neutral.300',
        text: 'Not sharing',
      },
    }),
    [],
  );

  const rows = useMemo(
    () =>
      admins.map((admin) => ({
        id: admin.id,
        name: (
          <Flex sx={{ gap: '16px', alignItems: 'center' }}>
            <Flex
              sx={{
                gap: '8px',
                text: {
                  fontSize: '28px',
                },
              }}
              alignItems="center"
              justifyContent="flex-start"
              pl="8px"
            >
              <Box width="40px" height="40px" minWidth="40px">
                <ProfilePicture width="40px" height="40px" imageUrl={admin.avatarUrl} name={admin.name} />
              </Box>
              <Box>
                <Flex sx={{ gap: '8px' }}>
                  <Text
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    fontSize="14px"
                    fontHeight="1.4"
                    color={!admin.onboardingCompleted ? 'text.subtle' : 'text.dark'}
                    fontWeight="500"
                  >
                    {admin.name}
                  </Text>

                  {!admin.onboardingCompleted && (
                    <Text
                      color="orange.600"
                      fontSize="14px"
                      fontWeight="500"
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      Invitation pending
                    </Text>
                  )}
                </Flex>
                <Text
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  fontSize="14px"
                  fontHeight="1.4"
                  color="text.subtle"
                  fontWeight="400"
                >
                  {admin.email}
                </Text>
              </Box>
            </Flex>
            <Flex width="max-content">
              {!admin.onboardingCompleted && isCompany && (
                <Flex sx={{ gap: '4px' }}>
                  <Text color="text.subtle" fontSize="14px">
                    Invitation not accepted.{' '}
                  </Text>
                  {(!isResendingInvitation || userToResendInvitation?.id !== admin.id) && (
                    <Box as="a" color="text.subtle" fontSize="14px" onClick={() => onResendInvitation(admin)}>
                      Resend invitation
                    </Box>
                  )}

                  {isResendingInvitation && userToResendInvitation?.id === admin.id && (
                    <Box as="span" display="inline-flex" alignItems="center" ml="4px">
                      <Loader size="small" />
                    </Box>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
        ),

        role: (
          <Flex
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              justifyContent: 'flex-start',
              width: '100%',
            }}
            fontSize="14px"
            color="text.main"
          >
            {admin.role === 'ally' ? 'Ally' : 'Network admin'}
          </Flex>
        ),
        linkedinSyncStatus: (
          <Flex
            sx={{ gap: '8px' }}
            alignItems={
              ['active', 'paused', 'not_sharing'].includes(admin.linkedinSyncStatus) || !admin.onboardingCompleted
                ? 'center'
                : 'baseline'
            }
            pt="4px"
            fontSize="14px"
            height="100%"
          >
            {admin.onboardingCompleted && (
              <Box
                width="8px"
                height="8px"
                sx={{ borderRadius: '100%', gap: '4px' }}
                backgroundColor={statusMap[admin.linkedinSyncStatus]?.color}
              />
            )}
            {admin.linkedinSyncStatus === 'not_completed' && admin.onboardingCompleted && (
              <Box>
                <Text color="text.main">{statusMap[admin.linkedinSyncStatus]?.text} </Text>
                {admin.id !== currentUserId ? (
                  <Box as="a" color="text.main" onClick={() => onUpdateLinkedinSync(admin, 'help-connect')}>
                    Help {admin.name.split(' ')[0]}
                  </Box>
                ) : (
                  <Box as="a" color="text.main" onClick={() => onUpdateLinkedinSync(admin, 'connect')}>
                    Connect
                  </Box>
                )}
              </Box>
            )}
            {admin.linkedinSyncStatus === 'reconnection_needed' && admin.onboardingCompleted && (
              <Text color="text.main">
                {statusMap[admin.linkedinSyncStatus]?.text}{' '}
                {admin.id !== currentUserId ? (
                  <Box as="a" color="text.main" onClick={() => onUpdateLinkedinSync(admin, 'help-reconnect')}>
                    Help {admin.name.split(' ')[0]}
                  </Box>
                ) : (
                  <Box as="a" color="text.main" onClick={() => onUpdateLinkedinSync(admin, 'reconnect')}>
                    Reconnect
                  </Box>
                )}
              </Text>
            )}
            {admin.onboardingCompleted &&
              ['active', 'paused', 'not_sharing', 'not_shared'].includes(admin.linkedinSyncStatus) && (
                <Text color="text.main">{statusMap[admin.linkedinSyncStatus]?.text}</Text>
              )}
            {!admin.onboardingCompleted && (
              <Flex sx={{ gap: '4px' }}>
                <Text color="text.subtle" fontSize="14px">
                  Invitation not accepted.{' '}
                </Text>
                {(!isResendingInvitation || userToResendInvitation?.id !== admin.id) && (
                  <Box as="a" color="text.subtle" fontSize="14px" onClick={() => onResendInvitation(admin)}>
                    Resend invitation
                  </Box>
                )}

                {isResendingInvitation && userToResendInvitation?.id === admin.id && (
                  <Box as="span" display="inline-flex" alignItems="center" ml="4px">
                    <Loader size="small" />
                  </Box>
                )}
              </Flex>
            )}
          </Flex>
        ),
        lastSyncedAt: (
          <Flex
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              justifyContent: 'flex-start',
              width: '100%',
            }}
            fontSize="14px"
            color="text.main"
          >
            {admin.lastSyncedAt ?? '-'}
          </Flex>
        ),
        contactsSynced: (
          <Flex width="100%" justifyContent="flex-start" fontSize="14px" color="text.main" my="8px" lineHeight="24px">
            {admin.contactsSynced}
          </Flex>
        ),
        actions: (
          <AdminDataTableActionItem
            ref={menuRef}
            actions={actions
              .filter((item) => item.show?.(admin))
              .map((action, index) => ({
                ...action,
                id: index,
                title: typeof action.title === 'function' ? action.title(admin) : action.title,
                onClick: () => action.onClick(admin),
              }))}
            tableItem={admin}
          />
        ),
      })),
    [
      actions,
      admins,
      currentUserId,
      isCompany,
      onResendInvitation,
      onUpdateLinkedinSync,
      statusMap,
      isResendingInvitation,
      userToResendInvitation,
    ],
  );

  return {
    columns: columns.filter((column) => column.show),
    rows,
    actions,
  };
};
